// Pure logic for the fee-management grid filter (FindReplaceFilter).
//
// Multi-value columns (states/counties/zips, and branches) hold several values
// in one comma-delimited cell. The filter must (a) offer each individual value
// as an option and (b) match with OR semantics — a row passes if it contains
// ANY selected value — rather than exact-matching the whole joined cell string
// (which would hide every multi-value row).

import { FEE_MGMT_COLUMNS } from '../constants/Columns'
import { splitEscapedBranches, parseBranchCellNames } from './branchHelpers'

export const BLANKS_OPTION = '(Blanks)'

// Is this a multi-value column (cells may hold several values)? Branch is
// multi-value via escaped commas; states/counties/zips via shouldSplitCommaValues.
export const isMultiValueColumn = (field, colDef) =>
  field === FEE_MGMT_COLUMNS.BRANCH.columnKey || !!colDef?.context?.shouldSplitCommaValues

// Break a cell into its individual values for option-building and matching.
// - Branch: when the known branch names are supplied, parse with
//   parseBranchCellNames so a LONE comma-named branch ("Boston, MA", stored raw)
//   resolves via whole-cell match instead of shattering on its comma. Without
//   names (e.g. mapping not loaded yet) fall back to the escape-aware split,
//   which still handles plain and escaped combos.
// - Other multi-value columns (shouldSplitCommaValues): plain comma split.
// - Single-value columns: the whole trimmed value, as one entry.
export const decomposeCellValue = (field, value, colDef, branchNames = null) => {
  const str = value == null ? '' : value.toString()
  if (str.trim() === '') {
    return []
  }
  if (field === FEE_MGMT_COLUMNS.BRANCH.columnKey) {
    return branchNames ? parseBranchCellNames(str, branchNames) : splitEscapedBranches(str)
  }
  if (colDef?.context?.shouldSplitCommaValues) {
    return str
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
  }
  return [str.trim()]
}

// Pre-compute the selection once per filter change (instead of per row): the
// lowercased set of selected values and whether "(Blanks)" is selected.
export const prepareFilter = (findText) => ({
  selected: new Set((findText || []).filter((t) => t !== BLANKS_OPTION).map((t) => t.toLowerCase())),
  wantsBlanks: !!findText && findText.includes(BLANKS_OPTION),
})

// OR predicate against a prepared selection: a row passes if any of its
// decomposed cell values is selected (case-insensitive), or "(Blanks)" is
// selected and the cell is empty.
export const cellMatchesPrepared = (field, value, colDef, prepared, branchNames = null) => {
  const str = value == null ? '' : value.toString()
  const isBlank = str.trim() === ''
  if (prepared.wantsBlanks && isBlank) {
    return true
  }
  if (isBlank || prepared.selected.size === 0) {
    return false
  }
  return decomposeCellValue(field, str, colDef, branchNames).some((v) => prepared.selected.has(v.toLowerCase()))
}

// Convenience: prepare + match in one call (used by tests / non-hot paths).
export const cellPassesFilter = (field, value, colDef, findText, branchNames = null) => {
  if (!findText || findText.length === 0) {
    return false
  }
  return cellMatchesPrepared(field, value, colDef, prepareFilter(findText), branchNames)
}

// Build the sorted list of unique filter options for a column, decomposing
// multi-value cells. Centralized so every builder (initial, on-filter-change,
// post-replace) stays consistent — including the "(Blanks)" detection for both
// truly-empty cells and non-empty cells that decompose to nothing.
export const buildUniqueValues = (api, field, colDef, branchNames = null) => {
  const valuesSet = new Set()
  let hasBlankValues = false

  api.forEachNode((node) => {
    const value = node.data[field]
    if (value === null || value === undefined || value === '') {
      hasBlankValues = true
      return
    }
    const values = decomposeCellValue(field, value, colDef, branchNames)
    if (values.length === 0) {
      hasBlankValues = true
      return
    }
    values.forEach((item) => valuesSet.add(item))
  })

  const sortedValues = [...valuesSet].sort()
  if (hasBlankValues) {
    sortedValues.unshift(BLANKS_OPTION)
  }
  return sortedValues
}
